import { useState, useEffect, useCallback } from 'react';
import {
  Plus, X, Trash2, Loader2, ChevronDown, ChevronUp, Banknote, Truck, Edit3, AlertCircle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (n) => `${Number(n || 0).toFixed(2)} €`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
};

const SupplierCreditTab = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/credit/suppliers`);
      setSuppliers(res.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const totalOwed = suppliers.reduce((s, c) => s + (c.total_balance || 0), 0);

  if (loading) {
    return <p className="text-zinc-500 text-sm py-12 text-center">
      <Loader2 className="w-4 h-4 animate-spin inline" /> Chargement…
    </p>;
  }

  return (
    <div className="space-y-4" data-testid="supplier-credit-tab">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-white/10 bg-[color:var(--color-surface)] p-4">
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Total dû aux fournisseurs</p>
          <p className="text-white text-3xl font-light mt-1"
             style={{ fontFamily: 'Cormorant Garamond, serif' }}
             data-testid="supplier-total-balance">
            {fmtEur(totalOwed)}
          </p>
        </div>
        <div className="border border-white/10 bg-[color:var(--color-surface)] p-4">
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Fournisseurs enregistrés</p>
          <p className="text-white text-3xl font-light mt-1"
             style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {suppliers.length}
          </p>
        </div>
        <div className="flex items-center justify-end">
          <button type="button"
            onClick={() => setShowCreateForm(true)}
            data-testid="add-supplier-btn"
            className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)]">
            <Plus className="w-3.5 h-3.5" />
            Nouveau fournisseur
          </button>
        </div>
      </div>

      {suppliers.length === 0 ? (
        <div className="border border-white/10 bg-[color:var(--color-surface)] p-8 text-center">
          <Truck className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Aucun fournisseur enregistré pour le moment.</p>
        </div>
      ) : (
        <div className="border border-white/10 bg-[color:var(--color-surface)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-3 text-zinc-500 text-xs uppercase tracking-wider">Fournisseur</th>
                <th className="text-right p-3 text-zinc-500 text-xs uppercase tracking-wider">Solde dû</th>
                <th className="text-right p-3 text-zinc-500 text-xs uppercase tracking-wider">Factures ouvertes</th>
                <th className="text-right p-3 text-zinc-500 text-xs uppercase tracking-wider">Plus ancienne</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <SupplierRow
                  key={s.id}
                  supplier={s}
                  expanded={expandedId === s.id}
                  onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
                  onEdit={() => setEditingSupplier(s)}
                  onChanged={fetchList}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateForm && (
        <SupplierFormModal
          onClose={() => setShowCreateForm(false)}
          onSaved={() => { setShowCreateForm(false); fetchList(); }}
        />
      )}
      {editingSupplier && (
        <SupplierFormModal
          supplier={editingSupplier}
          onClose={() => setEditingSupplier(null)}
          onSaved={() => { setEditingSupplier(null); fetchList(); }}
        />
      )}
    </div>
  );
};

// =========================================================================

const SupplierRow = ({ supplier, expanded, onToggle, onEdit, onChanged }) => (
  <>
    <tr className="border-b border-white/5 hover:bg-white/[0.02]"
        data-testid={`supplier-row-${supplier.id}`}>
      <td className="p-3 cursor-pointer" onClick={onToggle}>
        <p className="text-white text-sm">{supplier.name}</p>
        <p className="text-zinc-500 text-xs">{supplier.contact}</p>
      </td>
      <td className="p-3 text-right text-[color:var(--color-primary)] font-medium cursor-pointer" onClick={onToggle}>
        {fmtEur(supplier.total_balance)}
      </td>
      <td className="p-3 text-right text-zinc-400 cursor-pointer" onClick={onToggle}>
        {supplier.open_invoices_count}
      </td>
      <td className="p-3 text-right text-zinc-400 cursor-pointer" onClick={onToggle}>
        {fmtDate(supplier.oldest_unpaid_date)}
      </td>
      <td className="p-3 text-right">
        <div className="flex items-center justify-end gap-2 text-zinc-500">
          <button type="button" onClick={onEdit}
            data-testid={`edit-supplier-${supplier.id}`}
            className="hover:text-[color:var(--color-primary)]">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={onToggle}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </td>
    </tr>
    {expanded && (
      <tr>
        <td colSpan={5} className="bg-black/40 p-4">
          <SupplierDetailPanel supplierId={supplier.id} onChanged={onChanged} />
        </td>
      </tr>
    )}
  </>
);

// =========================================================================

const SupplierDetailPanel = ({ supplierId, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/credit/suppliers/${supplierId}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erreur'));
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (loading) return <p className="text-zinc-500 text-sm">Chargement…</p>;
  if (!detail) return null;

  const openInvoices = detail.invoices.filter((i) => i.balance > 0.005);
  const closedInvoices = detail.invoices.filter((i) => i.balance <= 0.005);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button type="button"
          onClick={() => setShowInvoiceForm(true)}
          data-testid={`add-invoice-btn-${supplierId}`}
          className="flex items-center gap-2 border border-white/20 text-zinc-300 px-4 py-2 text-xs uppercase tracking-wider hover:border-white/40">
          <Plus className="w-3.5 h-3.5" /> Ajouter une facture
        </button>
        {openInvoices.length > 0 && (
          <button type="button"
            onClick={() => setShowPaymentForm(true)}
            data-testid={`pay-supplier-btn-${supplierId}`}
            className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)]">
            <Banknote className="w-3.5 h-3.5" /> Enregistrer un paiement
          </button>
        )}
      </div>

      {/* Open invoices */}
      {openInvoices.length > 0 && (
        <div>
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">Factures ouvertes</p>
          <ul className="space-y-1">
            {openInvoices.map((inv) => (
              <li key={inv.id}
                  className="flex items-center justify-between border border-white/5 bg-black p-3 text-sm"
                  data-testid={`invoice-${inv.id}`}>
                <div className="min-w-0">
                  <p className="text-white">{inv.invoice_number || '—'} · {inv.description || 'Sans description'}</p>
                  <p className="text-zinc-500 text-xs">
                    Émise le {fmtDate(inv.invoice_date)}
                    {inv.due_date && ` · Échéance ${fmtDate(inv.due_date)}`}
                    {' · Payé '} {fmtEur(inv.paid_amount)} / {fmtEur(inv.amount)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[color:var(--color-primary)] font-medium">{fmtEur(inv.balance)}</span>
                  {inv.paid_amount === 0 && (
                    <button type="button"
                      onClick={async () => {
                        if (!window.confirm('Supprimer cette facture ?')) return;
                        try {
                          await axios.delete(`${API_URL}/api/admin/credit/invoices/${inv.id}`);
                          fetchDetail(); onChanged?.();
                          toast.success('Facture supprimée');
                        } catch (e) {
                          toast.error(extractErrorMessage(e, 'Échec'));
                        }
                      }}
                      data-testid={`delete-invoice-${inv.id}`}
                      className="text-zinc-500 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {openInvoices.length === 0 && (
        <p className="text-zinc-500 text-sm italic text-center py-4">Aucune facture ouverte ✓</p>
      )}

      {closedInvoices.length > 0 && (
        <details>
          <summary className="text-zinc-500 text-[10px] uppercase tracking-widest cursor-pointer mb-2">
            Factures réglées ({closedInvoices.length})
          </summary>
          <ul className="space-y-1 mt-2 opacity-60">
            {closedInvoices.map((inv) => (
              <li key={inv.id}
                  className="flex items-center justify-between border border-white/5 bg-black p-2 text-xs">
                <span>{inv.invoice_number || '—'} · {fmtDate(inv.invoice_date)}</span>
                <span className="text-zinc-500">{fmtEur(inv.amount)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {detail.payments.length > 0 && (
        <div>
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">Paiements ({detail.payments.length})</p>
          <ul className="space-y-1 max-h-60 overflow-y-auto">
            {detail.payments.map((p) => (
              <li key={p.id}
                  className="flex items-center justify-between border border-white/5 bg-black p-2 text-xs"
                  data-testid={`supplier-payment-${p.id}`}>
                <div>
                  <p className="text-zinc-300">{fmtDate(p.paid_at)} {p.mode && `· ${p.mode}`}</p>
                  {p.notes && <p className="text-zinc-600 italic mt-0.5">{p.notes}</p>}
                </div>
                <span className="text-emerald-400 font-medium">{fmtEur(p.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showInvoiceForm && (
        <InvoiceFormModal
          supplierId={supplierId}
          onClose={() => setShowInvoiceForm(false)}
          onSaved={() => { setShowInvoiceForm(false); fetchDetail(); onChanged?.(); }}
        />
      )}
      {showPaymentForm && (
        <SupplierPaymentModal
          supplierId={supplierId}
          invoices={openInvoices}
          onClose={() => setShowPaymentForm(false)}
          onSaved={() => { setShowPaymentForm(false); fetchDetail(); onChanged?.(); }}
        />
      )}
    </div>
  );
};

// =========================================================================

const SupplierFormModal = ({ supplier, onClose, onSaved }) => {
  const [name, setName] = useState(supplier?.name || '');
  const [contact, setContact] = useState(supplier?.contact || '');
  const [notes, setNotes] = useState(supplier?.notes || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) {
      toast.error('Nom requis');
      return;
    }
    setSaving(true);
    try {
      const body = { name: name.trim(), contact: contact.trim(), notes: notes.trim() };
      if (supplier) {
        await axios.put(`${API_URL}/api/admin/credit/suppliers/${supplier.id}`, body);
        toast.success('Fournisseur mis à jour');
      } else {
        await axios.post(`${API_URL}/api/admin/credit/suppliers`, body);
        toast.success('Fournisseur créé');
      }
      onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
         data-testid="supplier-form-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {supplier ? 'Modifier' : 'Nouveau'} fournisseur
          </h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <input type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nom *"
            data-testid="supplier-name"
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          <input type="text" value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="Contact (téléphone, email…)"
            data-testid="supplier-contact"
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          <textarea value={notes} rows={3}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (facultatif)"
            data-testid="supplier-notes"
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none resize-none" />
        </div>

        <div className="flex gap-2 mt-5">
          <button type="button" onClick={submit}
            disabled={saving}
            data-testid="supplier-submit"
            className="flex-1 bg-[color:var(--color-primary)] text-black py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
            {saving ? '…' : 'Enregistrer'}
          </button>
          <button type="button" onClick={onClose}
            className="border border-white/20 text-zinc-400 px-5 py-2.5 text-xs uppercase tracking-wider hover:border-white/40">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

// =========================================================================

const InvoiceFormModal = ({ supplierId, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/credit/suppliers/${supplierId}/invoices`, {
        amount: parseFloat(amount),
        invoice_number: invoiceNumber.trim(),
        invoice_date: invoiceDate ? new Date(invoiceDate).toISOString() : undefined,
        due_date: dueDate ? new Date(dueDate).toISOString() : undefined,
        description: description.trim(),
      });
      toast.success('Facture ajoutée');
      onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Nouvelle facture
          </h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Montant € *</label>
              <input type="number" step="0.01" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="invoice-amount"
                className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            </div>
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">N° facture</label>
              <input type="text" value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                data-testid="invoice-number"
                className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Date facture</label>
              <input type="date" value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                data-testid="invoice-date"
                className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            </div>
            <div>
              <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Échéance</label>
              <input type="date" value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="invoice-due-date"
                className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
            </div>
          </div>
          <input type="text" value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (facultatif)"
            data-testid="invoice-description"
            className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
        </div>
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={submit}
            disabled={saving}
            data-testid="invoice-submit"
            className="flex-1 bg-[color:var(--color-primary)] text-black py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
            {saving ? '…' : 'Enregistrer'}
          </button>
          <button type="button" onClick={onClose}
            className="border border-white/20 text-zinc-400 px-5 py-2.5 text-xs uppercase tracking-wider hover:border-white/40">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

// =========================================================================

const SupplierPaymentModal = ({ supplierId, invoices, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('transfer');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const numAmount = parseFloat(amount) || 0;
  // FIFO preview
  const preview = (() => {
    let rem = numAmount;
    return invoices.map((inv) => {
      const applied = Math.min(rem, inv.balance);
      rem = Math.round((rem - applied) * 100) / 100;
      return { ...inv, applied: Math.round(applied * 100) / 100 };
    });
  })();
  const allocated = preview.reduce((s, i) => s + i.applied, 0);
  const excess = Math.max(0, Math.round((numAmount - allocated) * 100) / 100);

  const submit = async () => {
    if (numAmount <= 0) {
      toast.error('Montant invalide');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/credit/suppliers/${supplierId}/payments`, {
        amount: numAmount, mode, notes,
      });
      toast.success(
        excess > 0
          ? `Paiement enregistré (${allocated}€ alloué, ${excess}€ excédentaire)`
          : `Paiement enregistré (${numAmount}€)`
      );
      onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
         data-testid="supplier-payment-modal">
      <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Paiement fournisseur
          </h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Montant *</label>
            <input type="number" step="0.01" min="0" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="supplier-payment-amount"
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Mode</label>
            <select value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none">
              <option value="transfer">Virement</option>
              <option value="cash">Cash</option>
              <option value="card">Carte</option>
              <option value="other">Autre</option>
            </select>
          </div>
          <div>
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Notes</label>
            <input type="text" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-black border border-white/10 text-white px-3 py-2 text-sm focus:border-[color:var(--color-primary)] outline-none" />
          </div>
        </div>

        <p className="text-zinc-500 text-[10px] uppercase tracking-widest mb-2">Aperçu FIFO</p>
        <ul className="space-y-1 mb-4">
          {preview.map((inv) => (
            <li key={inv.id}
                className="flex items-center justify-between border border-white/5 bg-black p-2 text-xs">
              <div>
                <p className="text-white">{inv.invoice_number || '—'} · {fmtDate(inv.invoice_date)}</p>
                <p className="text-zinc-500 text-[10px]">Solde {fmtEur(inv.balance)}</p>
              </div>
              <span className={inv.applied > 0 ? 'text-emerald-400' : 'text-zinc-700'}>
                {inv.applied > 0 ? fmtEur(inv.applied) : '—'}
              </span>
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between mb-4 text-xs">
          <p className="text-zinc-500">Alloué : {fmtEur(allocated)} / {fmtEur(numAmount)}</p>
          {excess > 0 && (
            <p className="text-orange-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Excédent : {fmtEur(excess)}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <button type="button" onClick={submit}
            disabled={saving || numAmount <= 0}
            data-testid="supplier-payment-submit"
            className="flex-1 bg-[color:var(--color-primary)] text-black py-3 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40">
            {saving ? '…' : 'Enregistrer'}
          </button>
          <button type="button" onClick={onClose}
            className="border border-white/20 text-zinc-400 px-6 py-3 text-xs uppercase tracking-wider hover:border-white/40">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierCreditTab;
